import { useState } from "react";
import { Mail, Phone, MapPin, Send, LoaderCircle } from "lucide-react";
import { toast } from "sonner";
import { SEO } from "@/components/SEO";
import { Reveal, Eyebrow } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INFO = [
    { icon: Mail, label: "Email", value: "info@anainternationalpvt.com", href: "mailto:info@anainternationalpvt.com" },
    { icon: Phone, label: "Phone", value: "+91 87998 43634", href: "tel:+918799843634" },
    {
        icon: MapPin,
        label: "Registered Office",
        value: "S. No. 265/3A, Room No. 8, Jadhav Mala, Ahmednagar, Ahmed Nagar – 414001, Maharashtra, India",
    },
];

const inputCls =
    "w-full bg-white/[0.03] border border-white/10 rounded-xl px-5 py-3.5 text-sm text-[#f5f4f0] placeholder:text-[#6a6963] outline-none focus:border-[#c9a227]/60 transition-colors duration-300";

export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", company: "", subject: "", message: "" });
    const [sending, setSending] = useState(false);

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            const res = await fetch(`${API}/contact`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ...form, company: form.company || null }),
            });
            if (!res.ok) throw new Error("Request failed");
            toast.success("Message sent. Our team will get back to you shortly.");
            setForm({ name: "", email: "", company: "", subject: "", message: "" });
        } catch {
            toast.error("Could not send your message. Please email us directly at info@anainternationalpvt.com.");
        } finally {
            setSending(false);
        }
    };

    return (
        <div data-testid="contact-page">
            <SEO
                title="Contact ANA International Private Limited"
                description="Contact ANA International Private Limited — AI and digital technology company in Ahmednagar, Maharashtra, India. Email info@anainternationalpvt.com or call +91 87998 43634."
                path="/contact"
            />

            <section className="relative pt-40 pb-16 overflow-hidden">
                <div className="absolute inset-0 grid-bg" />
                <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
                    <Reveal>
                        <Eyebrow>Contact</Eyebrow>
                        <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl mt-6 leading-tight">
                            Get in <span className="gold-text">Touch.</span>
                        </h1>
                        <p className="text-[#a3a29b] text-base sm:text-lg leading-relaxed mt-7 max-w-2xl">
                            For product enquiries, partnerships, investor conversations or general questions —
                            reach out to ANA INTERNATIONAL PRIVATE LIMITED.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="pb-28">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-5 gap-10">
                    <div className="lg:col-span-2 space-y-5">
                        {INFO.map((item, i) => (
                            <Reveal key={item.label} delay={i * 0.08}>
                                <div className="card-line rounded-2xl p-7" data-testid={`contact-info-${item.label.toLowerCase().replace(/\s+/g, "-")}`}>
                                    <div className="flex items-center gap-3">
                                        <item.icon size={18} className="text-[#e8c766]" />
                                        <p className="mono-label !text-[10px]">{item.label}</p>
                                    </div>
                                    {item.href ? (
                                        <a href={item.href} className="block text-sm text-[#f5f4f0] mt-4 hover:text-[#e8c766] transition-colors leading-relaxed">
                                            {item.value}
                                        </a>
                                    ) : (
                                        <p className="text-sm text-[#d8d7d1] mt-4 leading-relaxed">{item.value}</p>
                                    )}
                                </div>
                            </Reveal>
                        ))}
                    </div>

                    <Reveal delay={0.1} className="lg:col-span-3">
                        <form onSubmit={submit} className="card-line rounded-3xl p-8 sm:p-10 space-y-5" data-testid="contact-form">
                            <div className="grid sm:grid-cols-2 gap-5">
                                <div>
                                    <label htmlFor="contact-name" className="mono-label !text-[10px] block mb-2.5">Name *</label>
                                    <input id="contact-name" required minLength={2} className={inputCls} placeholder="Your full name" value={form.name} onChange={update("name")} data-testid="contact-name-input" />
                                </div>
                                <div>
                                    <label htmlFor="contact-email" className="mono-label !text-[10px] block mb-2.5">Email *</label>
                                    <input id="contact-email" required type="email" className={inputCls} placeholder="you@example.com" value={form.email} onChange={update("email")} data-testid="contact-email-input" />
                                </div>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-5">
                                <div>
                                    <label htmlFor="contact-company" className="mono-label !text-[10px] block mb-2.5">Company (Optional)</label>
                                    <input id="contact-company" className={inputCls} placeholder="Your company" value={form.company} onChange={update("company")} data-testid="contact-company-input" />
                                </div>
                                <div>
                                    <label htmlFor="contact-subject" className="mono-label !text-[10px] block mb-2.5">Subject *</label>
                                    <input id="contact-subject" required minLength={2} className={inputCls} placeholder="How can we help?" value={form.subject} onChange={update("subject")} data-testid="contact-subject-input" />
                                </div>
                            </div>
                            <div>
                                <label htmlFor="contact-message" className="mono-label !text-[10px] block mb-2.5">Message *</label>
                                <textarea id="contact-message" required minLength={10} rows={6} className={`${inputCls} resize-none`} placeholder="Write your message..." value={form.message} onChange={update("message")} data-testid="contact-message-input" />
                            </div>
                            <button type="submit" disabled={sending} className="btn-gold disabled:opacity-60 disabled:pointer-events-none" data-testid="contact-submit-button">
                                {sending ? <LoaderCircle size={16} className="animate-spin" /> : <Send size={16} />}
                                {sending ? "Sending..." : "Send Message"}
                            </button>
                        </form>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
